/**
 * 사업장 구성의 층 이름 — 화면을 묶는 세 층(지역 › 사업장 › 위치)을 고객사가 무엇이라 부를지.
 *
 * 2026-10-26 사용자 결정("공장 등 언어를 더 범용적으로"): 기본은 업종을 타지 않는 **지역 › 사업장 › 위치**, 업종에
 * 맞는 말은 [사업장·위치] 화면에서 고른다(아래 묶음 또는 직접 입력). 저장 칸(`region`·`site`·`location`)과 API 는
 * 그대로라 고른 말은 글자에만 들어간다 — 같은 서버의 모든 화면(사업장·위치·모니터링·메뉴 부제)이 한 벌을 쓴다.
 *
 * - **셋째 층은 화면의 위치**다. 화면 추가·상세·목록 거르개의 "위치" 칸은 이름을 바꾸지 않는다 — 그 칸은 층이
 *   아니라 화면의 속성이고, 층 이름이 "층" 이어도 화면마다 적는 값은 여전히 그 화면의 위치다.
 * - 맨 위 한 칸은 **조직 이름**(회사·기관)이다. 고르는 말이 아니다.
 * - 빈 칸은 기본으로 채운다 — 서버는 빈 문자열을 "정하지 않음" 으로 돌려준다.
 */

export interface SiteLabels {
  region: string;
  site: string;
  location: string;
}

export const DEFAULT_SITE_LABELS: SiteLabels = { region: '지역', site: '사업장', location: '위치' };

/** 층 이름 한 칸의 최대 글자 수 — 서버(`routes/sites.ts`)와 같다. */
export const SITE_LABEL_MAX = 8;

export interface SiteLabelPreset {
  id: string;
  name: string;
  labels: SiteLabels;
}

/** 업종별 묶음 — 첫째가 기본(범용). 맞는 것이 없으면 직접 입력. */
export const SITE_LABEL_PRESETS: readonly SiteLabelPreset[] = [
  { id: 'general', name: '범용', labels: DEFAULT_SITE_LABELS },
  { id: 'manufacturing', name: '제조', labels: { region: '지역', site: '공장', location: '건물' } },
  { id: 'office', name: '오피스·공공기관', labels: { region: '지역', site: '사옥', location: '층' } },
  { id: 'retail', name: '유통·프랜차이즈', labels: { region: '권역', site: '매장', location: '구역' } },
  { id: 'campus', name: '병원·학교', labels: { region: '지역', site: '캠퍼스', location: '건물' } },
];

export const CUSTOM_PRESET = 'custom';

/** 서버의 값(빈 칸은 정하지 않음)을 화면에 쓸 한 벌로 — 빈 칸은 기본. */
export function resolveSiteLabels(raw: Partial<SiteLabels> | null | undefined): SiteLabels {
  return {
    region: raw?.region?.trim() || DEFAULT_SITE_LABELS.region,
    site: raw?.site?.trim() || DEFAULT_SITE_LABELS.site,
    location: raw?.location?.trim() || DEFAULT_SITE_LABELS.location,
  };
}

/** 저장할 값 — 기본과 같은 칸은 빈 문자열로 둔다(기본이 바뀌면 따라가게). */
export function storedSiteLabels(labels: SiteLabels): SiteLabels {
  const resolved = resolveSiteLabels(labels);
  return {
    region: resolved.region === DEFAULT_SITE_LABELS.region ? '' : resolved.region,
    site: resolved.site === DEFAULT_SITE_LABELS.site ? '' : resolved.site,
    location: resolved.location === DEFAULT_SITE_LABELS.location ? '' : resolved.location,
  };
}

export function sameSiteLabels(a: SiteLabels, b: SiteLabels): boolean {
  const x = resolveSiteLabels(a);
  const y = resolveSiteLabels(b);
  return x.region === y.region && x.site === y.site && x.location === y.location;
}

/** 지금 이름이 어느 묶음인가 — 셋 다 같은 묶음이 없으면 직접 입력. */
export function presetOf(labels: SiteLabels): string {
  return SITE_LABEL_PRESETS.find((preset) => sameSiteLabels(preset.labels, labels))?.id ?? CUSTOM_PRESET;
}

/** 층 이름으로 쓸 수 있는가 — 서버와 같은 규칙. 빈 문자열은 기본이라 된다. 안 되면 까닭. */
export function siteLabelProblem(value: string): string | null {
  const text = value.trim();
  if (text.length > SITE_LABEL_MAX) return `${SITE_LABEL_MAX}자까지입니다`;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f›<>]/.test(text)) return '쓸 수 없는 글자가 있습니다';
  return null;
}

/** 정하지 않은 지역·위치의 이름 — "지역 미지정", 유통이면 "권역 미지정". */
export const unnamedRegion = (labels: SiteLabels): string => `${labels.region} 미지정`;
export const unassignedLocation = (labels: SiteLabels): string => `${labels.location} 미지정`;

/* ───────────────────────── 조사 ───────────────────────── */

type Particle = '은' | '이' | '을' | '과' | '으로';
const PAIRS: Record<Particle, [withBatchim: string, without: string]> = {
  은: ['은', '는'],
  이: ['이', '가'],
  을: ['을', '를'],
  과: ['과', '와'],
  으로: ['으로', '로'],
};

/** 낱말 뒤에 받침에 맞는 조사를 붙인다 — '공장' + 은 → '공장은', '매장' + 으로 → '매장으로', '층' + 으로 → '층으로'. */
export function withParticle(word: string, particle: Particle): string {
  const last = word.trim().slice(-1);
  const code = last.charCodeAt(0) - 0xac00;
  const [withBatchim, without] = PAIRS[particle];
  if (!(code >= 0 && code <= 11171)) return `${word}${withBatchim}(${without})`;
  const final = code % 28;
  if (particle === '으로') return `${word}${final === 0 || final === 8 ? without : withBatchim}`;
  return `${word}${final === 0 ? without : withBatchim}`;
}

/**
 * 글 속의 자리표를 층 이름으로 — `{region}`·`{site}`·`{location}`, 조사가 붙으면 `{site:은}`·`{location:으로}`.
 * 메뉴 부제·도움말처럼 한 번 쓰고 마는 글에 쓴다.
 */
export function fillSiteLabels(text: string, labels: SiteLabels): string {
  return text.replace(
    /\{(region|site|location)(?::(은|이|을|과|으로))?\}/g,
    (_, key: keyof SiteLabels, particle?: Particle) => (particle ? withParticle(labels[key], particle) : labels[key]),
  );
}
